import { Annotation, END, START, StateGraph } from '@langchain/langgraph/web'
import { PromptTemplate } from "@langchain/core/prompts";
import { ChatOpenAI } from "@langchain/openai";
import { AssemblyAI, Transcript } from 'assemblyai'

import promptKeypoint from 'bundle-text:./res/prompt-keypoints.txt'
import promptKeypointLemur from 'bundle-text:./res/prompt-keypoints-lemur.txt'
import promptSummary2Mermaid from 'bundle-text:./res/prompt-summary2mermaid.txt'

const StateAnnotation = Annotation.Root({
    input: Annotation<string|File|null>,
    transcript: Annotation<string|Transcript>,
    summary: Annotation<string|null>,
    mindmap: Annotation<string|null>,
    progress: Annotation<( message: string ) => void>
  })
   

const transcribeFromAudio = (assemblyAIKey: string) => 
    async ( state: typeof StateAnnotation.State ) => {

        console.log( 'transcribeFromAudio' )
        state.progress( 'Transcribe from audio ...')

        const { input: audio } = state
        
        if( !audio ) {
            throw new Error( "audio is invalid" );
        }


        const client = new AssemblyAI({
            apiKey: assemblyAIKey
        })    
        
        const response = await client.transcripts.transcribe({ audio })

        if( response.status == 'error' ) {
            throw new Error( "Transcript error: " + response.error );
        }

        const { text: transcript } = response
        
        state.progress( 'Transcription completed!')

        return { transcript  }

    }

const extractKeypointsFromAudioTranscript = ( assemblyAIKey: string ) => 
    async ( state: typeof StateAnnotation.State ) => {
    
        console.log( 'extractKeypointsFromAudiotTranscript' )
        state.progress( 'Extract Keypoints from audio transcription ...')

        const { transcript } = state
        
        if( !transcript ) {
            throw new Error( "Transcript is empty" );
        }

        if( typeof(transcript) === 'string' ) {
            throw new Error( "Transcript is not valid Transcript object" );
        }

        if( !('audio_url' in transcript) ) {
            throw new Error( "Transcript is not valif Transcript object" );
        }

        const client = new AssemblyAI({
            apiKey: assemblyAIKey
        })    

        
        const { response } = await client.lemur.task({
            transcript_ids: [transcript.id],
            prompt: promptKeypointLemur,
            final_model: 'anthropic/claude-3-5-sonnet'
        })

        state.progress( 'Keypoints extracted!')

        return { summary: response }
    }


const extractKeypointsFromTranscript = (openAIApiKey: string) => 
    async ( state: typeof StateAnnotation.State ) => {
        console.log( 'extractKeypointsFromTranscript' )
        state.progress( 'Extract Keypoints from text transcription ...')

        let transcript:string|null = null

        if( state.transcript && typeof(state.transcript) === 'string' ) {
            transcript = state.transcript
        }
        else if( state.input && typeof(state.input) === 'string' ) {
            transcript = state.input
        }
        
        if( !transcript || transcript === "" ) {
            throw new Error( "Transcript is empty" );
        }

        const promptTpl = PromptTemplate.fromTemplate( promptKeypoint )

        const prompt = await promptTpl.invoke({ transcript });

        const model = new ChatOpenAI({
            openAIApiKey: openAIApiKey,
            model: "gpt-4o-mini",
            temperature: 0
        });

        const response = await model.invoke(prompt)

        state.progress( 'Keypoints extracted!')

        return { summary: response.content }
    }

const transformSummaryToMindmap = ( openAIApiKey: string ) => 
    
    async ( state: typeof StateAnnotation.State ) => {
        
        console.log( 'transformSummaryToMindmap' )
        state.progress( 'Generate mindmap from summary ...')

        const { summary } = state
        
        if( !summary|| summary === "" ) {
            throw new Error( "Summary is empty" );
        }

        const promptTpl = PromptTemplate.fromTemplate( promptSummary2Mermaid )

        const prompt = await promptTpl.invoke({ summary });

        const model = new ChatOpenAI({
            openAIApiKey: openAIApiKey,
            model: "gpt-4o-mini",
            temperature: 0
        });

        const response = await model.invoke(prompt)

        state.progress( 'Mindmap generated!')

        return { mindmap: response.content }
    }


async function mindmapToMermaid( state: typeof StateAnnotation.State ) {
    console.log( 'mindmapToMermaid' )
    state.progress( 'Convert mindmap to mermaid synatx ...')

    const { mindmap } = state
    
    if( !mindmap|| mindmap === "" ) {
        throw new Error( "Mindmap is empty" );
    }

    const mindmapUpdated = mindmap.replace(/^```(mermaid|html)\s*/, "").replace(/```$/, "").trim();

    console.log(mindmapUpdated);

    state.progress( 'Mmermaid diagram generated!')

    return { mindmap: mindmapUpdated };
}

export function buildGraph( { openAIApiKey, assemblyAIApiKey }:  { openAIApiKey?: string, assemblyAIApiKey?: string } ) {

    if( !openAIApiKey ) {
        throw new Error( "OPENAI_API_KEY is not set" );
    }
    if( !assemblyAIApiKey ) {
        throw new Error( "ASSEMBLYAI_API_KEY is not set" );
    }

    return new StateGraph(StateAnnotation)
        .addNode('transcribe-from-audio', transcribeFromAudio( assemblyAIApiKey))
        .addNode('keypoints-from-transcript', extractKeypointsFromTranscript( openAIApiKey ) )
        .addNode('keypoints-from-audio-transcript', extractKeypointsFromAudioTranscript( assemblyAIApiKey ) )
        .addNode('summary-to-mindmap', transformSummaryToMindmap( openAIApiKey))
        .addNode('mindmap-to-mermaid', mindmapToMermaid )
        .addConditionalEdges( START, ( state ) => {

            if( typeof( state.input ) === 'string' ) {
                return 'keypoints-from-transcript'                                    
            }

            if( state.input instanceof Blob ) {
                return 'transcribe-from-audio'
            }

            return END;

        } )
        // .addEdge( 'transcribe-from-audio', 'keypoints-from-audio-transcript' )
        // .addEdge( 'keypoints-from-audio-transcript', 'summary-to-mindmap' )
        .addEdge( 'transcribe-from-audio', 'keypoints-from-transcript' )
        .addEdge( 'keypoints-from-transcript', 'summary-to-mindmap' )
        .addEdge( 'summary-to-mindmap', 'mindmap-to-mermaid' )
        .addEdge( 'mindmap-to-mermaid', END )
   
}